import { useLocalStorageWithUpdater } from "./useLocalStorageWithUpdater";

type Key = string | number;

export type OffsetAssignmentMode =
  | "portfolioOffsetPage"
  | "offsetDetailPage"
  | "portfolioOverviewPage"
  | "scenarioOffsetModal";

export const offsetAssignmentStorageKey = (mode: OffsetAssignmentMode) =>
  `offset-assignments-table-${mode}`;

export const clearLocalStorageUntilLogout = [
  "greenMarketSnapshotChart",
  "greenMarketSnapshotZoom",
  "greenMarketProjectViewAs",
  "greenMarketProjectShowMap",
  "greenMarketProjectFilter",
  "greenMarketProjectCardPagination",
  "greenMarketProjectListPagination",
  "initiativeTable-filter",
  "portfolioOverviewTable-filter",
  "emissionsManagerInitiativeTableFilter",
  "emissionsManagerScenariosShowFromMyOrganisation",
  "emissionsManagerInitiativeCommentSortBy",
  "portfolioDetailOffsetsProductType",
  "portfolioFocus",
  "tradeTable",
  "emissionsManagerOffsetTableFilter",
  "emissionsManagerAssignmentOffsetTableFilter",
  "emissionsManagerOffsetCommentSortBy",
  "tradeTimelineTableFilter",
  "mainMenuOpenKeys",
  "mainMenuIsPanel",
  "counterpartiesTableFilter",
  "awayBrokerTableFilter",
  "dailycurvesEndOfDay",
  "dailycurvesCarbonTable",
  "dailycurvesEnvironmentalTableFilter",
  "dailycurvesElectricityTableFilter",
  "UserTable",
  "CertificateProjectTableFilter",
  "InitiativeTable",
  "EmissionScope1Table",
  "EmissionScope2Table",
  "EmissionScope3Table",
  "initiativeTable-pagination",
  "portfolioOverviewTable-pagination",
  "away-brokers-pagination",
  "counter-parties-pagination",
  "offset-assignments-table",
  offsetAssignmentStorageKey("offsetDetailPage"),
  offsetAssignmentStorageKey("portfolioOffsetPage"),
  offsetAssignmentStorageKey("portfolioOverviewPage"),
  offsetAssignmentStorageKey("scenarioOffsetModal"),
  "offset-bank-table",
  "db-activity",
  "locale",
  "emissionsManagerScenariosTable",
  "emissionsManagerScenariosOffsetTable",
  "createEditUserManagement",
  "emissionsManagerScenariosTableFilter",
  "emissionsManagerScenariosInitiativeTable",
  "companyManagementTable",
  "companyManagementFilters",
  "APITokenTable",
  "APITokenfilter",
  "addCompany",
  "userManagementTable",
  "userManagementFilter",
  "companyUserManagementTable",
  "companyUserManagementFilter",
  "analyticTable",
  "analyticFilter",
  "projectFinder",
  "subscriptionPackagesTable",
  "subscriptionPackagesTableFilter",
  "addSubscriptionPackages",
  "currentHoldingsTableFilter",
  "eligibilitiesTable",
  "eligibilityTableFilter",
  "reportsContent",
  "bespokeReportsContent",
  "publicationsTableFilter",
  "publicationsTable",
  "newsAndAlertsTable",
  "newsAndAlertsTableFilter",
  "insightNewsAndAlerts",
  "tradeTimelineSiderFilter",
  "dailyHistoryChartWidgetFilter",
  "dailyHistoryChartFilter",
  "wholesalePriceTable",
  "insightArticlesAndUpdates",
  "tradeTimelineTable",
  "marketsTradeHistory",
  "dailycurvesCarbonTableMarketOverview",
  "marketsReports",
  "newsAndAlertTable",
  "publicationsTable",
  "certificateprojectEligibilityTable",
  "livePriceTable",
  "marketsSnapshotTable",
  "livePriceAccuMarketOverview",
  "dailycurvesCarbonChartMarketOverview",
  "marketEnvironmentalOverview",
  "livePricesEnvironmentalAuMarketOverview",
  "marketCarbonAuReportContentOverview",
  "marketEnvironmentalAuReportContentOverview",
  "livePricesCarbonIntMarketOverview",
  "marketCarbonIntReportContentOverview",
  "noRememberKeyForCoreTable",
  "scenarioInitiativesResultsFilterTable",
  "scenarioOffsetResultsFilterTable",
  "dailycurvesElectricAsxMarketOverview",
  "dailycurvesElectricShapeMarketOverview",
  "livePriceAuPowerMarketOverview",
  "marketPowerAuReportContentOverview",
  "transactionHistoryFilter",
  "transactionHistoryTable",
  "analyticEventsFilter",
  "analyticEventsTable",
  "tradeTrackerTable",
  "tradeTrackerTableFilter",
  "registryEnvironmentalAu",
  "coreTableStorybookTest",
  "environmentalAuHoldingsTable",
  "environmentalAuHoldingsTableFilter",
  "environmentalAuHoldingsTableShowAll",
  "analyticEventsSubscribersFilter",
  "coretable",
  "emissionFilter",
  "sendDailyEmailLastUnsaveValue",
  "addPublication",
  "tradeableProductBidOrOfferSelector",
  "tradeableProductsFilter",
  "tradeableProductsTable",
  "aemoNoticeFilter",
  "marketPowerAemoNoticeFilter",
  "marketPowerAuEditProductFilter",
  "projectExplorerViewMode",
  "hourlyShapeCurveChartFilter",
  "registryTransfersFilter",
  "registryViewerHoldingsTable",
  "registryViewerHoldingsTableFilter",
  "registryViewerHoldingsTableShowAll",
  "projectRegistryHoldingsTable",
  "projectRegistryHoldingsTableFilter",
  "projectRegistryHoldingsTableShowAll",
  "performanceHistory",
  "performanceHistoryTable",
  "known-offtakes-settings",
  "knownOfftakesFilter",
  "offtakeBenchmarksVersion",
  "ppaBenchmarksCurrentLabel",
  "adminCentreChangeLogTable",
  "adminCentreChangeLogFilter",
  "adminCentreOrganisationsTable",
  "adminCentreOrganisationsTableFilter",
  "adminCentreRelatedNewsTable",
] as const;

export type LocalStorageUntilLogoutKey =
  typeof clearLocalStorageUntilLogout[number];

export const clearLocalStoragePerKey = (key: LocalStorageUntilLogoutKey) =>
  localStorage.removeItem(key);

export type UseLocalStorageUntilLogoutProps<T> = (props: {
  key: LocalStorageUntilLogoutKey;
  initialValue: T;
}) => [T, (input: ((prevState: T) => T) | T) => void];

export const useLocalStorageUntilLogout = <T>({
  key,
  initialValue,
}: Parameters<UseLocalStorageUntilLogoutProps<T>>[0]): ReturnType<
  UseLocalStorageUntilLogoutProps<T>
> => {
  const [curValue, setValue] = useLocalStorageWithUpdater<T>(key, initialValue);
  const value: T = curValue ?? initialValue;

  return [value, setValue];
};

export const useLocalStorageUntilLogoutPerKey = <T>(
  storageKey: LocalStorageUntilLogoutKey,
  { key, initialValue }: { key: Key; initialValue: T }
): [T, (newValue: T) => void] => {
  const [storageValue, setStorageValue] = useLocalStorageUntilLogout<
    Record<Key, T>
  >({
    key: storageKey,
    initialValue: { [key]: initialValue },
  });

  const valueForKey = storageValue?.[key] ?? initialValue;

  const updateValue = (newValue: T) => {
    setStorageValue((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  return [valueForKey, updateValue];
};
